import { useEffect, useRef, useCallback } from 'react';
import { useAssistant } from '@services/AssistantProvider';

const getWelcomeMessages = (step) => {
	switch (step) {
		case 2:
			return [
				"Hey! Great job completing your business details. Now let's connect your Google Business Profile so customers can find your products online.",
				'I can talk you through it, or feel free to dive in directly'
			];
		case 3:
			return [
				'Almost there! Just one more step to go 🚀',
				"Let's connect your POS system to sync your inventory automatically.",
				'Which POS system do you use? I can help you get connected'
			];
		default:
			return ['Hey! Welcome to your Dashboard 👋', 'Feel free to ask me anything!'];
	}
};

const getCurrentStep = (progress) => {
	if (!progress) return 1;
	if (!progress.businessDetailsCompleted) return 1;
	if (!progress.gbpCompleted) return 2;
	if (!progress.posConnectionCompleted) return 3;
	return 'complete';
};

export const useOnboardingAssistant = ({ onboardingProgress, connectionState, currentShop, authError, validationErrors }) => {
	const { messages, sessionId, setPageContext, triggerBotMessages, clearHistory, shownSteps, setShownSteps } = useAssistant();

	const welcomeStartedRef = useRef(false);
	const shownStepsRef = useRef(shownSteps);

	// Derive current step as a primitive so effects trigger reliably on step changes
	const currentStep = getCurrentStep(onboardingProgress);

	// Sync ref from provider state (restored from localStorage)
	useEffect(() => {
		shownStepsRef.current = shownSteps;
		if (messages.length > 0) {
			welcomeStartedRef.current = true;
		}
	}, [shownSteps, messages.length]);

	const markStepAsShown = useCallback(
		(step) => {
			const updated = new Set([...shownStepsRef.current, step]);
			shownStepsRef.current = updated;
			setShownSteps(updated);
		},
		[setShownSteps]
	);

	// Build page context with pre-formatted system prompt context
	useEffect(() => {
		const hasGBP = !!currentShop?.gmbConnection?.status;
		const hasPOS = !!currentShop?.inventorySources?.find((s) => s.kind === 'stock');
		const posProvider = connectionState?.stock?.provider?.label;

		const lines = [
			'The user is on the Unify onboarding page.',
			'',
			'ONBOARDING STEPS:',
			'  Step 1: Business Details — search for store, confirm address, enter full name and email, create a password, accept T&Cs, click "Get started".',
			'  Step 2: Connect Google Business Profile — click "Connect Google Business Profile", authorise via Google, wait for verification.',
			'  Step 3: Connect POS system — select POS from dropdown, then either authorise via OAuth, enter manual credentials (FTP/API key), or submit a partner request. Optional product data sync.',
			'',
			`CURRENT STATE:`,
			`  Current step: ${currentStep}`,
			`  Google Business Profile: ${hasGBP ? 'Connected' : 'Not yet connected'}`,
			`  POS system: ${hasPOS ? 'Connected' : 'Not yet connected'}`
		];

		if (posProvider) {
			lines.push(`  Selected POS provider: ${posProvider} (connection in progress)`);
			lines.push(`  Provide specific guidance for connecting ${posProvider}.`);
		}

		if (currentStep === 1) {
			lines.push(
				'',
				'The user needs to fill in their business details to get started. Help them find their store, complete the form, and click "Get started".'
			);

			// Add authentication and validation error context if present
			if (authError) {
				lines.push('', `VALIDATION ERROR: The user encountered an authentication error: "${authError}"`);
				lines.push('The user can see this error message in the UI. Help them resolve it.');
			}

			if (validationErrors && Object.keys(validationErrors).length > 0) {
				const errorMessages = Object.values(validationErrors).join(', ');
				lines.push('', `VALIDATION ERRORS: The user has form validation errors: ${errorMessages}`);
				lines.push('The user can see these errors in the UI. Help them complete the required fields correctly.');
			}
		} else if (currentStep === 2) {
			lines.push(
				'',
				'The user needs to connect their Google Business Profile. This verifies their shop and enables product listings on Google.'
			);
		} else if (currentStep === 3 && !posProvider) {
			lines.push(
				'',
				'The user needs to select and connect their POS/inventory system. Ask which system they use if they need help choosing.'
			);
		}

		setPageContext({ systemPromptContext: lines.join('\n') });
	}, [currentStep, connectionState, currentShop, authError, validationErrors, setPageContext]);

	// Show welcome messages on initial load
	useEffect(() => {
		if (welcomeStartedRef.current || messages.length > 0 || !sessionId) return;

		welcomeStartedRef.current = true;

		const welcomeTexts = getWelcomeMessages(currentStep);

		if (typeof currentStep === 'number') {
			markStepAsShown(currentStep);
		}

		triggerBotMessages(welcomeTexts);
	}, [sessionId, currentStep, messages.length, triggerBotMessages, markStepAsShown]);

	// Show transition messages on step changes
	useEffect(() => {
		if (!sessionId || messages.length === 0) return;

		if (typeof currentStep !== 'number' || shownStepsRef.current.has(currentStep)) {
			return;
		}

		markStepAsShown(currentStep);

		const transitionMessages = getWelcomeMessages(currentStep);
		triggerBotMessages(transitionMessages);
	}, [currentStep, sessionId, messages.length, triggerBotMessages, markStepAsShown]);

	// Clear history when onboarding completes
	useEffect(() => {
		if (currentStep === 'complete') {
			clearHistory();
			welcomeStartedRef.current = false;
		}
	}, [currentStep, clearHistory]);
};
